// Redis service for user progress caching and session management
import { Redis } from '@upstash/redis';

// Redis client instance
let redisClient: Redis | null = null;
let redisInitialized = false;

// Performance monitoring
const performanceMetrics = {
  cacheHits: 0,
  cacheMisses: 0,
  totalRequests: 0,
  averageResponseTime: 0,
  lastRequestTime: 0
};

// Check if we're in a browser environment
const isBrowser = typeof window !== 'undefined';

// Initialize Redis client
const initRedis = async () => {
  if (redisInitialized) return redisClient !== null;
  
  try {
    // In browser environments, always use localStorage fallback
    // Redis client is only for server-side use
    if (isBrowser) {
      console.log('Browser environment detected, using localStorage fallback');
      redisInitialized = true;
      return false;
    }

    // For server-side, use Redis client
    const redisUrl = process.env.UPSTASH_REDIS_REST_URL;
    const redisToken = process.env.UPSTASH_REDIS_REST_TOKEN;
    
    if (redisUrl && redisToken) {
      console.log('Redis credentials found for server environment');
      
      try {
        redisClient = new Redis({
          url: redisUrl,
          token: redisToken,
        });
        console.log('Redis client initialized successfully');
        redisInitialized = true;
        return true;
      } catch (redisError) {
        console.warn('Failed to create Redis client:', redisError);
        redisInitialized = true;
        return false;
      }
    } else {
      console.log('Redis credentials not found for server environment');
      redisInitialized = true;
      return false;
    }
  } catch (error) {
    console.warn('Redis initialization failed:', error);
    redisInitialized = true;
    return false;
  }
};

// Initialize Redis on module load
initRedis();

// User progress cache keys
const USER_PROGRESS_KEY = (userId: string) => `user:progress:${userId}`;
const USER_SESSION_KEY = (userId: string) => `user:session:${userId}`;
const USER_AUTH_KEY = (userId: string) => `user:auth:${userId}`;
const METHODOLOGY_CACHE_KEY = (userId: string) => `user:methodology:${userId}`;

// Cache duration (24 hours)
const CACHE_DURATION = 24 * 60 * 60;

// Performance monitoring helper
const trackPerformance = (operation: string, startTime: number) => {
  const duration = Date.now() - startTime;
  performanceMetrics.totalRequests++;
  performanceMetrics.lastRequestTime = Date.now();
  
  // Update average response time
  performanceMetrics.averageResponseTime = 
    (performanceMetrics.averageResponseTime * (performanceMetrics.totalRequests - 1) + duration) / performanceMetrics.totalRequests;
  
  console.log(`Redis ${operation} completed in ${duration}ms`);
  return duration;
};

export interface CachedUserProgress {
  userId: string;
  methodology?: {
    methodologyId: string;
    subMethodologyId?: string;
    title: string;
    completedItems: string[];
    lastAccessed: string;
  };
  guidedMethodology?: {
    methodologyId: string;
    title: string;
    completedSteps: string[];
    lastStepIndex: number;
    lastAccessed: string;
  };
  updatedAt: string;
}

export interface CachedUserSession {
  userId: string;
  deviceId: string;
  lastActive: string;
  methodologyId?: string;
  subMethodologyId?: string;
  type?: 'straight-up' | 'guided';
}

export interface CachedUserAuth {
  userId: string;
  email: string;
  displayName: string;
  lastLogin: string;
  deviceId: string;
}

export interface CachedMethodologyData {
  userId: string;
  lastSession?: {
    methodologyId: string;
    subMethodologyId?: string;
    lastAccessed: string;
    title: string;
    type: 'straight-up' | 'guided';
    completedSteps?: string[];
    lastStepIndex?: number;
  };
  cachedAt: string;
}

export const redisService = {
  // Check if Redis is available
  isAvailable: (): boolean => {
    return redisClient !== null;
  },

  // Get performance metrics
  getPerformanceMetrics: () => {
    return {
      ...performanceMetrics,
      cacheHitRate: performanceMetrics.totalRequests > 0 
        ? (performanceMetrics.cacheHits / performanceMetrics.totalRequests * 100).toFixed(2) + '%'
        : '0%'
    };
  },

  // Test Redis connection
  testConnection: async (): Promise<boolean> => {
    if (!redisClient) {
      console.log('Redis client not available');
      return false;
    }
    
    try {
      const startTime = Date.now();
      await redisClient.set('test_connection', 'ok', { ex: 60 });
      const result = await redisClient.get('test_connection');
      const duration = Date.now() - startTime;
      
      console.log(`Redis connection test: ${result === 'ok' ? 'SUCCESS' : 'FAILED'} in ${duration}ms`);
      return result === 'ok';
    } catch (error) {
      console.error('Redis connection test failed:', error);
      return false;
    }
  },

  // Cache user progress with performance tracking
  cacheUserProgress: async (userId: string, progress: CachedUserProgress): Promise<boolean> => {
    const startTime = Date.now();
    
    if (!redisClient) {
      // Fallback to localStorage for client-side caching
      try {
        const key = `rawpa_user_progress_${userId}`;
        localStorage.setItem(key, JSON.stringify(progress));
        trackPerformance('cacheUserProgress (localStorage)', startTime);
        return true;
      } catch (error) {
        console.error('Error caching user progress in localStorage:', error);
        return false;
      }
    }
    
    try {
      const key = USER_PROGRESS_KEY(userId);
      await redisClient.set(key, JSON.stringify(progress), { ex: CACHE_DURATION });
      trackPerformance('cacheUserProgress (Redis)', startTime);
      return true;
    } catch (error) {
      console.error('Error caching user progress:', error);
      return false;
    }
  },

  // Get cached user progress with performance tracking
  getCachedUserProgress: async (userId: string): Promise<CachedUserProgress | null> => {
    const startTime = Date.now();
    
    if (!redisClient) {
      // Fallback to localStorage
      try {
        const key = `rawpa_user_progress_${userId}`;
        const cached = localStorage.getItem(key);
        if (cached) {
          performanceMetrics.cacheHits++;
          trackPerformance('getCachedUserProgress (localStorage hit)', startTime);
          return JSON.parse(cached);
        }
        performanceMetrics.cacheMisses++;
        trackPerformance('getCachedUserProgress (localStorage miss)', startTime);
        return null;
      } catch (error) {
        console.error('Error getting cached user progress from localStorage:', error);
        return null;
      }
    }
    
    try {
      const key = USER_PROGRESS_KEY(userId);
      const cached = await redisClient.get(key);
      if (cached) {
        performanceMetrics.cacheHits++;
        trackPerformance('getCachedUserProgress (Redis hit)', startTime);
        return JSON.parse(cached as string);
      }
      performanceMetrics.cacheMisses++;
      trackPerformance('getCachedUserProgress (Redis miss)', startTime);
      return null;
    } catch (error) {
      console.error('Error getting cached user progress:', error);
      return null;
    }
  },

  // Cache methodology data for faster retrieval
  cacheMethodologyData: async (userId: string, data: CachedMethodologyData): Promise<boolean> => {
    const startTime = Date.now();
    
    if (!redisClient) {
      try {
        const key = `rawpa_methodology_data_${userId}`;
        localStorage.setItem(key, JSON.stringify(data));
        trackPerformance('cacheMethodologyData (localStorage)', startTime);
        return true;
      } catch (error) {
        console.error('Error caching methodology data in localStorage:', error);
        return false;
      }
    }
    
    try {
      const key = METHODOLOGY_CACHE_KEY(userId);
      await redisClient.set(key, JSON.stringify(data), { ex: CACHE_DURATION });
      trackPerformance('cacheMethodologyData (Redis)', startTime);
      return true;
    } catch (error) {
      console.error('Error caching methodology data:', error);
      return false;
    }
  },

  // Get cached methodology data
  getCachedMethodologyData: async (userId: string): Promise<CachedMethodologyData | null> => {
    const startTime = Date.now();
    
    if (!redisClient) {
      try {
        const key = `rawpa_methodology_data_${userId}`;
        const cached = localStorage.getItem(key);
        if (cached) {
          performanceMetrics.cacheHits++;
          trackPerformance('getCachedMethodologyData (localStorage hit)', startTime);
          return JSON.parse(cached);
        }
        performanceMetrics.cacheMisses++;
        trackPerformance('getCachedMethodologyData (localStorage miss)', startTime);
        return null;
      } catch (error) {
        console.error('Error getting cached methodology data from localStorage:', error);
        return null;
      }
    }
    
    try {
      const key = METHODOLOGY_CACHE_KEY(userId);
      const cached = await redisClient.get(key);
      if (cached) {
        performanceMetrics.cacheHits++;
        trackPerformance('getCachedMethodologyData (Redis hit)', startTime);
        return JSON.parse(cached as string);
      }
      performanceMetrics.cacheMisses++;
      trackPerformance('getCachedMethodologyData (Redis miss)', startTime);
      return null;
    } catch (error) {
      console.error('Error getting cached methodology data:', error);
      return null;
    }
  },

  // Cache user session with performance tracking
  cacheUserSession: async (userId: string, session: CachedUserSession): Promise<boolean> => {
    const startTime = Date.now();
    
    if (!redisClient) {
      // Fallback to localStorage
      try {
        const key = `rawpa_user_session_${userId}`;
        localStorage.setItem(key, JSON.stringify(session));
        trackPerformance('cacheUserSession (localStorage)', startTime);
        return true;
      } catch (error) {
        console.error('Error caching user session in localStorage:', error);
        return false;
      }
    }
    
    try {
      const key = USER_SESSION_KEY(userId);
      await redisClient.set(key, JSON.stringify(session), { ex: CACHE_DURATION });
      trackPerformance('cacheUserSession (Redis)', startTime);
      return true;
    } catch (error) {
      console.error('Error caching user session:', error);
      return false;
    }
  },

  // Get cached user session with performance tracking
  getCachedUserSession: async (userId: string): Promise<CachedUserSession | null> => {
    const startTime = Date.now();
    
    if (!redisClient) {
      // Fallback to localStorage
      try {
        const key = `rawpa_user_session_${userId}`;
        const cached = localStorage.getItem(key);
        if (cached) {
          performanceMetrics.cacheHits++;
          trackPerformance('getCachedUserSession (localStorage hit)', startTime);
          return JSON.parse(cached);
        }
        performanceMetrics.cacheMisses++;
        trackPerformance('getCachedUserSession (localStorage miss)', startTime);
        return null;
      } catch (error) {
        console.error('Error getting cached user session from localStorage:', error);
        return null;
      }
    }
    
    try {
      const key = USER_SESSION_KEY(userId);
      const cached = await redisClient.get(key);
      if (cached) {
        performanceMetrics.cacheHits++;
        trackPerformance('getCachedUserSession (Redis hit)', startTime);
        return JSON.parse(cached as string);
      }
      performanceMetrics.cacheMisses++;
      trackPerformance('getCachedUserSession (Redis miss)', startTime);
      return null;
    } catch (error) {
      console.error('Error getting cached user session:', error);
      return null;
    }
  },

  // Cache user authentication info
  cacheUserAuth: async (userId: string, auth: CachedUserAuth): Promise<boolean> => {
    if (!redisClient) {
      // Fallback to localStorage
      try {
        const key = `rawpa_user_auth_${userId}`;
        localStorage.setItem(key, JSON.stringify(auth));
        console.log(`User auth cached in localStorage for ${userId}`);
        return true;
      } catch (error) {
        console.error('Error caching user auth in localStorage:', error);
        return false;
      }
    }
    
    try {
      const key = USER_AUTH_KEY(userId);
      await redisClient.set(key, JSON.stringify(auth), { ex: CACHE_DURATION });
      console.log(`User auth cached for ${userId}`);
      return true;
    } catch (error) {
      console.error('Error caching user auth:', error);
      return false;
    }
  },

  // Get cached user auth
  getCachedUserAuth: async (userId: string): Promise<CachedUserAuth | null> => {
    if (!redisClient) {
      // Fallback to localStorage
      try {
        const key = `rawpa_user_auth_${userId}`;
        const cached = localStorage.getItem(key);
        if (cached) {
          console.log(`Retrieved cached user auth from localStorage for ${userId}`);
          return JSON.parse(cached);
        }
        return null;
      } catch (error) {
        console.error('Error getting cached user auth from localStorage:', error);
        return null;
      }
    }
    
    try {
      const key = USER_AUTH_KEY(userId);
      const cached = await redisClient.get(key);
      if (cached) {
        console.log(`Retrieved cached user auth for ${userId}`);
        return JSON.parse(cached as string);
      }
      return null;
    } catch (error) {
      console.error('Error getting cached user auth:', error);
      return null;
    }
  },

  // Generate device ID
  generateDeviceId: (): string => {
    // Try to get from localStorage first
    let deviceId = localStorage.getItem('rawpa_device_id');
    if (!deviceId) {
      // Generate a new device ID
      deviceId = `device_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      localStorage.setItem('rawpa_device_id', deviceId);
    }
    return deviceId;
  },

  // Clear user cache
  clearUserCache: async (userId: string): Promise<boolean> => {
    if (!redisClient) {
      // Fallback to localStorage
      try {
        const keys = [
          `rawpa_user_progress_${userId}`,
          `rawpa_user_session_${userId}`,
          `rawpa_user_auth_${userId}`
        ];
        
        keys.forEach(key => localStorage.removeItem(key));
        console.log(`Cleared localStorage cache for user ${userId}`);
        return true;
      } catch (error) {
        console.error('Error clearing localStorage cache:', error);
        return false;
      }
    }
    
    try {
      const progressKey = USER_PROGRESS_KEY(userId);
      const sessionKey = USER_SESSION_KEY(userId);
      const authKey = USER_AUTH_KEY(userId);
      
      await Promise.all([
        redisClient.del(progressKey),
        redisClient.del(sessionKey),
        redisClient.del(authKey)
      ]);
      
      console.log(`Cleared cache for user ${userId}`);
      return true;
    } catch (error) {
      console.error('Error clearing user cache:', error);
      return false;
    }
  },

  // Sync user progress between Redis and Firestore
  syncUserProgress: async (userId: string, firestoreProgress: any): Promise<boolean> => {
    if (!redisClient) {
      // Fallback to localStorage sync
      try {
        const key = `rawpa_user_progress_${userId}`;
        const cached = localStorage.getItem(key);
        
        if (cached) {
          const cachedProgress = JSON.parse(cached);
          const mergedProgress = {
            ...firestoreProgress,
            methodology: firestoreProgress.methodology || cachedProgress.methodology,
            guidedMethodology: firestoreProgress.guidedMethodology || cachedProgress.guidedMethodology,
            updatedAt: new Date().toISOString()
          };
          
          localStorage.setItem(key, JSON.stringify(mergedProgress));
          console.log(`Synced user progress in localStorage for ${userId}`);
          return true;
        }
        
        return false;
      } catch (error) {
        console.error('Error syncing user progress in localStorage:', error);
        return false;
      }
    }
    
    try {
      const cachedProgress = await redisService.getCachedUserProgress(userId);
      
      if (cachedProgress) {
        // Merge cached progress with Firestore progress
        const mergedProgress = {
          ...firestoreProgress,
          methodology: firestoreProgress.methodology || cachedProgress.methodology,
          guidedMethodology: firestoreProgress.guidedMethodology || cachedProgress.guidedMethodology,
          updatedAt: new Date().toISOString()
        };
        
        // Cache the merged progress
        await redisService.cacheUserProgress(userId, mergedProgress);
        console.log(`Synced user progress for ${userId}`);
        return true;
      }
      
      return false;
    } catch (error) {
      console.error('Error syncing user progress:', error);
      return false;
    }
  }
}; 